import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, ImageDown, Maximize2, RotateCcw } from "lucide-react";
import { api, API, errMsg, downloadFile } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty, useSettings } from "@/components/common";
import { Button } from "@/components/ui/button";
import { LicenseCard } from "@/components/LicenseCard";
import { PersonalData, RenewalDialog, RenewalList } from "@/components/MyArea";

const renewFrom = (l) => {
  const d = new Date(`${l.expiry_date}T12:00:00`);
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
};

export default function MyLicense() {
  const { user } = useAuth();
  const settings = useSettings();
  const [lics, setLics] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [qr, setQr] = useState(null);
  const [renew, setRenew] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const loadRenewals = () => api.get("/renewals").then((r) => setRenewals(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/licenses").then((r) => setLics(r.data)).catch(() => setLics([]));
    loadRenewals();
    if (user.athlete_id) api.get(`/athletes/${user.athlete_id}`).then((r) => setAthlete(r.data)).catch(() => {});
  }, [user.athlete_id]);
  if (!lics) return <Loading />;
  const dl = (p, n) => downloadFile(p, n).catch((e) => toast.error(errMsg(e)));

  return (
    <div>
      <PageHeader eyebrow="Area personale" title={`Ciao, ${user.name}`} subtitle="La tua licenza sportiva digitale. Mostra il QR code all'organizzatore della gara." />
      {athlete && <PersonalData a={athlete} />}
      {lics.length === 0 ? <Empty>Non risultano licenze associate al tuo profilo.</Empty> : (
        <div className="space-y-8">
          {lics.map((l) => {
            const issued = !!l.license_number && l.status !== "pending";
            return (
              <div key={l.id} className="grid gap-4 lg:grid-cols-[1.5fr_1fr]">
                <LicenseCard lic={l} settings={settings} />
                <div className="card-surface h-fit space-y-3 p-5 no-print">
                  <div className="font-display text-base font-semibold">{l.discipline_name}</div>
                  <div className="text-sm text-slate-600">Stato: <b>{l.status_label}</b> · scadenza {fmtDate(l.expiry_date)}</div>
                  {issued && <>
                    <Button onClick={() => setQr(l)} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid={`my-show-qr-btn-${l.id}`}><Maximize2 className="mr-2 h-4 w-4" />Mostra QR code</Button>
                    <Button variant="outline" onClick={() => dl(`/licenses/${l.id}/pdf`, `Licenza-${l.license_number}.pdf`)} className="w-full" data-testid={`my-pdf-btn-${l.id}`}><FileDown className="mr-2 h-4 w-4" />Scarica PDF</Button>
                    <Button variant="outline" onClick={() => dl(`/licenses/${l.id}/qr.png`, `QR-${l.license_number}.png`)} className="w-full" data-testid={`my-qr-download-btn-${l.id}`}><ImageDown className="mr-2 h-4 w-4" />Scarica QR</Button>
                  </>}
                  {["active", "expired"].includes(l.status) && (
                    renewals.some((r) => r.license_id === l.id && r.status === "inviata")
                      ? <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid={`my-renewal-pending-${l.id}`}>Richiesta di rinnovo in attesa del settore</div>
                      : l.status === "active" && renewFrom(l) > new Date().toISOString().slice(0, 10)
                        ? <div className="text-xs text-slate-500" data-testid={`my-renewal-from-${l.id}`}>Il rinnovo si potrà richiedere dal {fmtDate(renewFrom(l))}</div>
                        : <Button variant="outline" onClick={() => setRenew(l)} className="w-full" data-testid={`my-renewal-btn-${l.id}`}><RotateCcw className="mr-2 h-4 w-4" />Richiedi rinnovo al settore</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <RenewalList rows={renewals} />
      <RenewalDialog lic={renew} onOpenChange={(v) => !v && setRenew(null)} onDone={loadRenewals} />
      {qr && (
        <button onClick={() => setQr(null)} className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-white p-6" data-testid="my-qr-fullscreen">
          <img src={`${API}/licenses/${qr.id}/qr.png`} alt="QR code" className="w-full max-w-sm" />
          <div className="font-mono text-lg font-semibold text-[#0F2042]">{qr.license_number}</div>
          <div className="text-sm text-slate-500">Tocca per chiudere</div>
        </button>
      )}
    </div>
  );
}
